/**
 * Handoff for the eModal *link* step, shared by useFixPlateFlow and
 * useRescheduleFlow.
 *
 * Both actions need the same three eModal IDs. When the appointment was
 * typed in by hand we don't have them, so before the action can run the
 * extension has to find that appointment inside the operator's eModal
 * account and report its IDs back (see extension/content/
 * emodal-link-assistant.js). The extension POSTs them to
 * /appointments/:id/emodal-link itself, and the resulting
 * appointment_updated socket event is what tells the page to continue.
 */

import type { EmodalLinkSearch } from '../services/appointmentApi';

const EXT_ID = import.meta.env.VITE_CHROME_EXT_ID || 'nniodjlikchoomnhdbcnlfncdcpodbib';

export interface EmodalLinkStartPayload {
  sessionId: string;
  appointmentId: number;
  /** Where the operator's eModal appointment list lives. */
  listUrl: string;
  search: EmodalLinkSearch;
  /** 'fix_plate' | 'reschedule' — only used for the on-page wording. */
  intent: string;
  userJwt: string;
}

/**
 * The @types/chrome globals aren't installed, and the sibling flows work
 * around that by reaching for `window.chrome` untyped. Narrowing it here
 * once keeps this module type-clean without pulling in the dependency.
 */
interface ChromeRuntimeLike {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback: (response: { ok?: boolean; error?: string } | undefined) => void
  ) => void;
  lastError?: { message?: string };
}

function getExtensionRuntime(): ChromeRuntimeLike | null {
  const c = (window as unknown as { chrome?: { runtime?: ChromeRuntimeLike } }).chrome;
  return c?.runtime?.sendMessage ? c.runtime : null;
}

export function sendEmodalLinkStart(
  payload: EmodalLinkStartPayload
): Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    const runtime = getExtensionRuntime();
    if (!runtime) {
      resolve({ ok: false, error: 'Chrome runtime unavailable' });
      return;
    }
    try {
      runtime.sendMessage(
        EXT_ID,
        { type: 'EMODAL_LINK_START', ...payload },
        (resp) => {
          if (runtime.lastError) {
            resolve({ ok: false, error: runtime.lastError.message });
          } else {
            resolve({ ok: !!resp?.ok, error: resp?.error });
          }
        }
      );
    } catch (e) {
      resolve({ ok: false, error: (e as Error).message });
    }
  });
}

/**
 * Human-readable instruction shown in the sticky toast while the
 * operator is on eModal. Deliberately concrete: the whole flow hinges on
 * them opening the right appointment.
 */
export function linkInstruction(search: EmodalLinkSearch): string {
  const bits: string[] = [];
  if (search.ticket) bits.push(`ticket ${search.ticket}`);
  if (search.containerNumber) bits.push(`container ${search.containerNumber}`);
  const what = bits.length ? bits.join(' / ') : 'this appointment';
  const type = search.apptTypeLabel ? ` (${search.apptTypeLabel})` : '';
  return `Open ${what}${type} on eModal — we'll link it automatically`;
}
